<template>
  <form ref="formElement" @submit.prevent="submit">
    <section class="card__body">
      <h4>{{ language.title_account_and_rep_details }}</h4>
      <div class="row">
        <div class="col-md-6">
          <SaxxText name="accountName" :form="formName" required>{{ language.label_account_name }}</SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText name="businessPhoneNumber" :form="formName" required>{{ language.label_business_phone }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText name="repName" :form="formName" required>{{ language.label_rep_name }}</SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText name="repEmail" type="email" :form="formName" required>{{ language.label_rep_email }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText
            name="customerEmail"
            type="email"
            :form="formName"
            required
          >{{ language.label_customer_email }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <SaxxTextArea name="accountProfile" :form="formName" rows="3" required>{{ language.label_account_profile }}</SaxxTextArea>
        </div>
      </div>
    </section>

    <section class="card__body">
      <h4>{{ language.title_address }}</h4>

      <div class="row">
        <div class="col-md-6">
          <SaxxSelect name="country" :form="formName" :items="countries" required>{{ language.label_country }}</SaxxSelect>
        </div>

        <div class="col-md-6">
          <SaxxSelect
            v-if="provinces.length > 0"
            name="province"
            :form="formName"
            :items="provinces"
            required
          >
            <template v-if="fields.country === 'USA'">
              {{ language.label_state }}
            </template>
            <template v-else>{{ language.label_province }}</template>
          </SaxxSelect>
          <SaxxText v-else name="province" :form="formName">{{ language.label_province }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText name="accountAddress" maxlength="35" :form="formName" required>{{ language.label_address_line_1 }}</SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText name="accountAddress2" maxlength="20" :form="formName">{{ language.label_address_line_2 }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText name="city" :form="formName" required>{{ language.label_city }}</SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText name="postalCode" :form="formName" required>{{ language.label_postal_code }}</SaxxText>
        </div>
      </div>
    </section>

    <section class="card__body">
      <h4>{{ language.title_business_info }}</h4>

      <div class="row">
        <div class="col-md-6 agency">
          <SaxxSelect name="agency" :form="formName" :items="agencies" required>{{ language.label_agency }}</SaxxSelect>
        </div>

        <div class="col-md-6">
          <SaxxSelect name="channel" :form="formName" :items="channels" required>{{ language.label_channel }}</SaxxSelect>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText name="website" :form="formName">{{ language.label_retailer_website }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText
            name="openingOrder"
            :form="formName"
            :validator="v => Number(v) >= 0"
            pattern="\d*"
            required
          >{{ language.label_opening_order_forecast }}</SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText
            name="calendarYear"
            :form="formName"
            :validator="v => Number(v) >= 0"
            pattern="\d*"
            required
          >{{ language.label_opening_year_forecast }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText
            pattern="\d*"
            name="percentOfBusinessInApparel"
            :form="formName"
            :validator="v => Number(v) >= 0 && Number(v) <= 100"
            required
          >{{ language.label_percent_of_business_in_apparel }}</SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText name="keyBrand" :form="formName" required>{{ language.label_key_brands }}</SaxxText>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12">
          {{language.label_image_type_notice}}
        </div>
      </div>
      <div class="row">
        <div class="col-md-6">
          <slot name="insideImages">
            <SaxxUpload
              multiple
              name="insideImages"
              :form="formName"
              @change="
                files => {
                  images.inside = files;
                }
              "
            >{{ language.label_image_inside_store }}</SaxxUpload>
          </slot>
        </div>

        <div class="col-md-6">
          <slot name="outsideImages">
            <SaxxUpload
              multiple
              name="outsideImages"
              :form="formName"
              @change="
                files => {
                  images.outside = files;
                }
              "
            >{{ language.label_image_outside_store }}</SaxxUpload>
          </slot>
        </div>
      </div>
    </section>

    <section class="card__body">
      <slot>
        <div class="row">
          <div class="col-12">
            <button class="btn btn-saxx" type="submit" :disabled="isLoading">{{ language.label_submit }}</button>
          </div>
        </div>
      </slot>
    </section>
  </form>
</template>

<script>
  import { ErrorlessRepository as axios } from "../../repositories/Repository";
  import { mapActions, mapGetters } from "vuex";
  import { language } from '../../lang';

  import SaxxText from "../forms/SaxxText";
  import SaxxSelect from "../forms/SaxxSelect";
  import SaxxTextArea from "../forms/SaxxTextArea";
  import SaxxUpload from "../forms/SaxxUpload";

  export default {
    name: "AccountForm",
    components: {
      SaxxText,
      SaxxSelect,
      SaxxUpload,
      SaxxTextArea,
    },
    props: {
      formName: {
        required: true,
        type: String,
      },
      lang: {
        required: false,
        type: String,
        default: 'en',
      },
      additionalFields: {
        required: false,
        type: Object,
        default: () => {},
      },
    },
    created() {
      this.createForm({
        name: this.formName,
        fields: {
          ...this.additionalFields,

          repName: "",
          repEmail: "",

          accountName: "",
          customerEmail: "",
          accountProfile: "",
          businessPhoneNumber: "",

          country: "",
          city: "",
          postalCode: "",
          province: "",
          accountAddress: "",
          accountAddress2: "",

          keyBrand: "",
          agency: "",
          channel: "",
          website: "",

          openingOrder: 0,
          calendarYear: 0,
          percentOfBusinessInApparel: 0,

          insideImages: [],
          outsideImages: [],

          language: this.lang
        }
      });
      this.fetchChannels();
      this.fetchCountries();
    },
    data() {
      return {
        agencies: [],
        channels: [],
        countries: [],
        provinces: [],
      };
    },
    computed: {
      ...mapGetters("forms", ["getFields", "getStatus"]),
      fields() {
        return this.getFields({
          form: this.formName
        });
      },
      isLoading() {
        return this.getStatus({ form: this.formName }) === "loading";
      },
      isReviewForm() {
        return this.formName.includes(".review");
      },
      language() {
        const lang = language(this.lang);

        return lang.creditDashboard;
      },
    },
    methods: {
      ...mapActions("forms", ["createForm", "setErrors"]),
      async fetchChannels(currentChannel = null) {
        const params = {
          context: this.isReviewForm ? "review" : "new",
        };

        if (this.isReviewForm && currentChannel) {
          params.current = currentChannel;
        }

        const response = await axios.get("/credit-dashboard/metadata/channels", { params });

        this.channels = response.data;
      },
      async fetchCountries() {
        const response = await axios.get("/credit-dashboard/metadata/countries");

        this.countries = response.data;
      },
      async fetchProvinces(country) {
        const response = await axios.get(
          `/credit-dashboard/metadata/countries/${country}/provinces`
        );

        this.provinces = response.data;
      },
      async fetchAgencies(country) {
        const response = await axios.get(
          `/credit-dashboard/metadata/countries/${country}/agencies`
        );

        this.agencies = response.data;
      },
      submit() {
        if (!this.$refs.formElement.checkValidity()) {
          return this.$refs.formElement.reportValidity();
        }

        this.setErrors({
          form: this.formName,
          errors: [],
        });

        this.$emit('submit', {
          ...this.fields,
        });
      },
    },
    watch: {
      "fields.country": function(country) {
        if (!String(country).trim().length) {
          return;
        }

        this.fetchAgencies(country);
        this.fetchProvinces(country);
      },
      "fields.channel": function(channel) {
        if (!this.isReviewForm || !String(channel || "").trim().length) {
          return;
        }

        this.fetchChannels(channel);
      }
    },
  };
</script>
